// Hooks de dados da agência: visão consolidada e marca própria (fatia S5b).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AccountBrandingResponse, AccountBrandingUpdate, AgencyOverviewResponse, CompanyBrandingResponse } from '@impulso/shared/api/agency'
import { api } from '../../lib/api'

export function useAgencyOverview(accountId: string | undefined) {
  return useQuery({
    queryKey: ['agency-overview', accountId],
    queryFn: () => api.get<AgencyOverviewResponse>(`/agency/${accountId}/overview`),
    enabled: !!accountId,
    refetchInterval: 5 * 60_000,
  })
}

/** Marca exibida para uma empresa (agência com marca própria ou Impulso). Use no portal do cliente e em relatórios. */
export function useCompanyBranding(companyId: string | undefined) {
  return useQuery({
    queryKey: ['company-branding', companyId],
    queryFn: () => api.get<CompanyBrandingResponse>(`/companies/${companyId}/branding`),
    enabled: !!companyId,
    staleTime: 10 * 60_000,
    retry: false,
  })
}

export function useAccountBranding(accountId: string) {
  return useQuery({
    queryKey: ['account-branding', accountId],
    queryFn: () => api.get<AccountBrandingResponse>(`/accounts/${accountId}/branding`),
  })
}

function invalidateBrand(qc: ReturnType<typeof useQueryClient>, accountId: string) {
  void qc.invalidateQueries({ queryKey: ['account-branding', accountId] })
  void qc.invalidateQueries({ queryKey: ['company-branding'] })
}

export function useSaveBranding(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AccountBrandingUpdate) => api.put<{ ok: true }>(`/accounts/${accountId}/branding`, body),
    onSuccess: () => invalidateBrand(qc, accountId),
  })
}

export function useUploadLogo(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return api.upload<{ logoUrl: string }>(`/accounts/${accountId}/branding/logo`, form)
    },
    onSuccess: () => invalidateBrand(qc, accountId),
  })
}
